/* Supply › Reports › Stock › Batchwise Stock Report
 * On-hand stock by batch with valuation and expiry status.
 * Data: GET /v1/inventory/warehouse/{id} (paged + optional search). */
import React, { useEffect, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum, cbExpiryStatus } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack, RackCell } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const StockBatchwise = () => {
  /* The store comes from the topbar, app-wide — see Layout/WarehouseContext. */
  const { code: wh, isAll } = useWarehouse();
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageState, setPageState] = useState({ page: 0, size: 50, totalPages: 0, totalElements: 0 });
  const setPageField = (key, val) =>
    setPageState((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [searchTerm, wh]);

  useEffect(() => {
    if (!wh) {
      setLots([]);
      setLoading(false);
      /* No all-stores stock query exists — the endpoint takes the warehouse in its
         path. An empty table would read as "no stock", so say what is actually
         needed. */
      setError(
        isAll
          ? "Stock is held per store, so this report needs a single one. Pick a store in the topbar."
          : null
      );
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInventoryLots(wh, { page: pageState.page, size: pageState.size, search: searchTerm })
      .then((res) => {
        if (!alive) return;
        setLots(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load stock report.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, isAll, searchTerm, pageState.page, pageState.size, reloadKey]);

  const pageUnits = lots.reduce((s, l) => s + l.qtyA, 0);
  const pageValue = lots.reduce((s, l) => s + l.qtyA * l.purchase, 0);

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  const handleExport = () => {
    const headers = ["Medicine", "Code", "Batch", "Expiry", "Rack", "Qty available", "Qty received", "Purchase", "MRP", "Stock value (MRP)", "Warehouse"];
    const rows = lots.map((l) => [
      l.medicineName || "",
      l.medId || "",
      l.batch || "",
      l.exp || "",
      /* Where to walk to find it — the point of exporting this at all. */
      l.rackCode || "",
      l.qtyA,
      l.qtyR,
      l.purchase,
      l.mrp,
      Number((l.qtyA * l.mrp).toFixed(2)),
      l.wh || "",
    ]);
    exportToXlsx(`batchwise-stock-${wh || "all"}.xlsx`, headers, rows, "Stock");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.STOCK_REPORT} label="Stock Reports" />
      <SectionHeader
        title="Batchwise Stock Report"
        sub="On-hand stock by batch with valuation and expiry status"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!lots.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search medicine, code, or batch…"
          style={{ width: 280, maxWidth: "100%" }}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} SKUs
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="SKUs in stock" value={cbFmtNum(pageState.totalElements)} variant="ink" />
        <KpiBox label="Units (this page)" value={cbFmtNum(pageUnits)} variant="ink" />
        <KpiBox label="Stock cost (this page)" value={cbFmtINR(pageValue)} variant="green" />
      </div>

      {loading ? (
        <LoadingState label="Loading stock report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              { label: "Medicine" },
              { label: "Code" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "Rack" },
              { label: "Qty available", align: "right" },
              { label: "Purchase", align: "right" },
              { label: "MRP", align: "right" },
              { label: "Stock value", align: "right" },
              { label: "Status" },
            ]}
          >
            {lots.length === 0 && (
              <tr>
                <Td colSpan={10} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No stock found.
                </Td>
              </tr>
            )}
            {lots.map((l) => {
              const exp = cbExpiryStatus(l.exp);
              return (
                <Tr key={l.id}>
                  <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName}</Td>
                  <Td mono>{l.medId}</Td>
                  <Td mono>{l.batch}</Td>
                  <Td mono>{l.exp}</Td>
                  <RackCell lot={l} />
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtNum(l.qtyA)}
                    <span style={{ color: "var(--fg-subtle)" }}> / {cbFmtNum(l.qtyR)}</span>
                  </Td>
                  <Td mono right style={{ color: "var(--fg-muted)" }}>
                    {cbFmtINR(l.purchase)}
                  </Td>
                  <Td mono right>
                    {cbFmtINR(l.mrp)}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(l.qtyA * l.mrp)}
                  </Td>
                  <Td>
                    <Pill variant={exp.status} dot>
                      {exp.label}
                    </Pill>
                  </Td>
                </Tr>
              );
            })}
          </Table>

          <ToolbarBar>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={pageState.page <= 0}
                onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default StockBatchwise;
